import { Link, Outlet } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChatTeardropDotsIcon, PaperPlaneTiltIcon, StarIcon } from '@phosphor-icons/react';
import { Logo, LogoMark } from '@growhero/brand';
import { legalUrl } from '@/lib/legal';
import { LanguageToggle, ThemeToggle } from '../shell/UserBar';

/** Pillars of the side panel, each in its product-area colour (field variants inside the panel). */
const PILLARS = [
  { key: 'reviews', icon: StarIcon, cls: 'text-area-reviews' },
  { key: 'messages', icon: PaperPlaneTiltIcon, cls: 'text-area-messages' },
  { key: 'ai', icon: ChatTeardropDotsIcon, cls: 'text-area-ai' },
] as const;

/**
 * Split layout for sign-in/up (A3): the form on the cool stone canvas (left), the deep sea-blue
 * brand field (right) with a heavy Commissioner statement whose second sentence is saffron, a tonal
 * laurel bleeding off the edge, and the three product areas. No testimonials or invented numbers
 * (ARCHITECTURE §11).
 */
export function AuthLayout() {
  const { t } = useTranslation('auth');
  return (
    <div className="grid min-h-dvh bg-canvas lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      <div className="flex min-h-dvh flex-col px-6 py-6 sm:px-10">
        <header className="flex items-center justify-between">
          <Link
            to="/login"
            aria-label="GrowHero"
            className="rounded-sm focus-visible:outline-2 focus-visible:outline-focus"
          >
            <Logo height={28} title="GrowHero" />
          </Link>
          <div className="flex items-center gap-1">
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </header>
        <main className="flex flex-1 items-center justify-center py-12">
          <div className="w-full max-w-[25rem] animate-rise">
            <Outlet />
          </div>
        </main>
        <footer className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-ink-muted">
          <span>© {new Date().getFullYear()} GrowHero</span>
          <a href={legalUrl('terms')} className="hover:text-ink">
            {t('layout.terms')}
          </a>
          <a href={legalUrl('privacy')} className="hover:text-ink">
            {t('layout.privacy')}
          </a>
        </footer>
      </div>

      <aside
        className="surface-field relative hidden overflow-hidden lg:flex lg:flex-col"
        data-surface="field"
      >
        {/* The mark as a large tonal shape, bleeding off the edge (A3 signature). */}
        <LogoMark
          size={720}
          tone="mono"
          title={null}
          className="pointer-events-none absolute -bottom-44 -right-52 text-field-tonal"
        />
        <div className="relative my-auto px-14 py-16">
          <LogoMark size={48} tone="field" title={null} className="mb-9" />
          <h2 className="display-heavy max-w-[15ch] text-[3.25rem] leading-[1.02] text-on-field">
            {t('layout.headline1')}{' '}
            <span className="block text-field-accent">{t('layout.headline2')}</span>
          </h2>
          <p className="mt-6 max-w-[44ch] text-lede leading-relaxed text-on-field-muted">
            {t('layout.body')}
          </p>
          <dl className="mt-12 grid max-w-xl grid-cols-3 gap-6 border-t border-field-line pt-6">
            {PILLARS.map(({ key, icon: Icon, cls }) => (
              <div key={key}>
                <dt className="flex items-center gap-2 text-[1.0625rem] font-extrabold tracking-headline text-on-field">
                  <Icon weight="duotone" className={`size-5 shrink-0 ${cls}`} aria-hidden />
                  {t(`layout.pillars.${key}.title`)}
                </dt>
                <dd className="mt-1.5 text-sm leading-snug text-on-field-subtle">
                  {t(`layout.pillars.${key}.body`)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </aside>
    </div>
  );
}
